"use client";

import React, { useEffect, useState } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Rocket, ChevronLeft, ChevronRight, ChevronDown, User, Layers, History, Clock, ListChecks, Tag, Send, AlertTriangle, FastForward, PlusCircle, RefreshCw, Trash2, Archive, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "./ui/badge";

const ActivityTimelineTab = ({
    contactActivity,
    loading,
    activityPagination,
    fetchContactActivity,
    contactId,
    renderEventIcon,
    renderEventData,
    getTimeAgo,
}: any) => {
    const [filter, setFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    // 🔥 Sync with server-side filters and pagination
    useEffect(() => {
        if (contactId) {
            fetchContactActivity(contactId, {
                page: currentPage,
                action: filter === "all" ? undefined : filter
            });
        }
    }, [contactId, fetchContactActivity, filter, currentPage]);

    const handleFilterChange = (newFilter: string) => {
        setFilter(newFilter);
        setCurrentPage(1); // Reset to first page on filter change
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const toggleExpand = (id: string) => {
        setExpandedId(expandedId === id ? null : id);
    };

    // Helper to render value (as chip if it's emailStatus)
    const renderValue = (field: string, value: any) => {
        if (value === undefined || value === null || value === "") return <span className="text-zinc-400">None</span>;

        if (field === "emailStatus") {
            const status = String(value);
            const statusColors: Record<string, string> = {
                safe: "bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
                risky: "bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
                invalid: "bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20",
                bounced: "bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20",
                unverified: "bg-zinc-50 text-zinc-600 border-zinc-100 dark:bg-zinc-500/10 dark:text-zinc-400 dark:border-zinc-500/20",
            };

            return (
                <Badge
                    variant="outline"
                    className={cn(
                        "text-[10px] font-bold uppercase tracking-tight py-0.5",
                        statusColors[status.toLowerCase()] || "bg-zinc-50 text-zinc-600 border-zinc-100 dark:bg-zinc-500/10 dark:text-zinc-400"
                    )}
                >
                    {status.replace("_", " ")}
                </Badge>
            );
        }

        if (field === "isActive" || field === "isActiveContact") {
            return (
                <Badge
                    variant="outline"
                    className={cn(
                        "text-[10px] font-bold uppercase tracking-tight py-0.5",
                        value ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-rose-50 text-rose-600 border-rose-100"
                    )}
                >
                    {value ? "Active" : "Disabled"}
                </Badge>
            );
        }

        return <span className="truncate max-w-[150px] inline-block">{String(value)}</span>;
    };

    const actionOptions = [
        { value: "all", label: "All Activities" },
        { value: "contact_created", label: "Created" },
        { value: "contact_updated", label: "Updated" },
        { value: "status_changed", label: "Status Changed" },
        { value: "email_status_changed", label: "Email Status" },
        { value: "added_to_list", label: "Added to List" },
        { value: "removed_from_list", label: "Removed from List" },
        { value: "reverified_email", label: "Reverified" },
        { value: "email_verified", label: "Email Verified" },
        { value: "campaign_sent", label: "Campaign Sent" },
        { value: "campaign_failed", label: "Campaign Failed" },
        { value: "campaign_skipped", label: "Campaign Skipped" },
        { value: "email_archived", label: "Archived" },
        { value: "email_restored", label: "Restored" },
        { value: "deleted", label: "Deleted" },
    ];

    const actionColorMap: any = {
        contact_created: "text-green-500 bg-green-500/10",
        email_verified: "text-green-500 bg-green-500/10",
        reverified_email: "text-green-500 bg-green-500/10",

        contact_updated: "text-blue-500 bg-blue-500/10",
        status_changed: "text-blue-500 bg-blue-500/10",
        email_status_changed: "text-blue-500 bg-blue-500/10",

        added_to_list: "text-indigo-500 bg-indigo-500/10",
        removed_from_list: "text-indigo-500 bg-indigo-500/10",

        campaign_sent: "text-emerald-500 bg-emerald-500/10",

        campaign_failed: "text-red-500 bg-red-500/10",
        deleted: "text-red-500 bg-red-500/10",

        campaign_skipped: "text-yellow-500 bg-yellow-500/10",

        email_archived: "text-gray-500 bg-gray-500/10",
        email_restored: "text-gray-500 bg-gray-500/10",
    };

    return (
        <TabsContent
            value="userActivityLogs"
            className="h-full mt-0 focus-visible:outline-none overflow-hidden flex flex-col"
        >
            {/* 🔥 Filter Bar */}
            <div className="sticky top-0 z-20 dark:bg-zinc-950 px-6 py-2 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Activity Logs
                </h3>

                <select
                    value={filter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                    className="text-xs border border-zinc-200 dark:border-zinc-800 rounded-md px-2 py-1 bg-white dark:bg-zinc-900 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                    {actionOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            <div className="flex-1 overflow-y-auto hide-scrollbar pb-8">
                {/* Timeline Container */}
                <div className="space-y-1 relative pr-2 px-6 mt-4">

                    {/* Timeline line */}
                    <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-zinc-100 dark:bg-zinc-800 hidden md:block" />

                    {loading && contactActivity.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                className="p-4 rounded-full bg-primary/10"
                            >
                                <Activity className="h-8 w-8 text-primary" />
                            </motion.div>
                            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest animate-pulse">
                                Syncing Activities...
                            </p>
                        </div>
                    ) : !Array.isArray(contactActivity) || contactActivity.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-50">
                            <Rocket className="h-10 w-10 text-zinc-300" />
                            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">
                                No activity found
                            </p>
                        </div>
                    ) : (
                        <div className={cn("space-y-0", loading && "opacity-50 pointer-events-none")}>
                            {contactActivity.map((log: any, i: number) => {
                                const isExpanded = expandedId === log.id;
                                return (
                                    <motion.div
                                        key={log.id || i}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                        className="relative pl-0 md:pl-14 group"
                                    >
                                        {/* Timeline bubble */}
                                        <div className="absolute left-[21px] top-6 h-2.5 w-2.5 rounded-full bg-zinc-200 dark:bg-zinc-700 border-2 border-white dark:border-zinc-900 transition-colors group-hover:bg-primary z-10 hidden md:block" />

                                        <div
                                            onClick={() => toggleExpand(log.id)}
                                            className={cn(
                                                "mb-4 bg-white dark:bg-zinc-900/50 rounded-2xl border border-zinc-100 dark:border-zinc-800 shadow-sm transition-all cursor-pointer hover:shadow-md hover:border-zinc-200 dark:hover:border-zinc-700 overflow-hidden",
                                                isExpanded && "border-primary/30 dark:border-primary/30 ring-1 ring-primary/10"
                                            )}
                                        >
                                            {/* Header - Always visible */}
                                            <div className="p-4 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700">
                                                        {renderEventIcon(log.action)}
                                                    </div>

                                                    <div>
                                                        <h4 className="text-sm font-bold text-zinc-900 dark:text-zinc-50 capitalize">
                                                            {renderEventData(log)}
                                                        </h4>
                                                        <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-bold uppercase">
                                                            <span>{getTimeAgo(log.createdAt)}</span>
                                                            {log.performedBy?.name && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>{log.performedBy.name}</span>
                                                                </>
                                                            )}
                                                            <span>•</span>
                                                            <span
                                                            // className={`
                                                            //         capitalize text-xs font-semibold
                                                            //         inline-flex items-center px-3 py-1 rounded-full
                                                            //          text-gray-500
                                                            //     `}
                                                            >
                                                                {log.action.replaceAll("_", " ")}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    <motion.div
                                                        animate={{ rotate: isExpanded ? 180 : 0 }}
                                                        className="text-zinc-400"
                                                    >
                                                        <ChevronDown className="h-4 w-4" />
                                                    </motion.div>
                                                </div>
                                            </div>

                                            {/* Accordion Content */}
                                            <AnimatePresence>
                                                {isExpanded && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50"
                                                    >
                                                        <div className="p-4 pt-2 space-y-4">
                                                            {/* Activity Message */}
                                                            <div className="pl-1">
                                                                {/* <p
                                                                    className={`
                                                                        capitalize text-md font-bold
                                                                        inline-flex items-center px-3 py-1 rounded-2
                                                                        ${actionColorMap[log.action] || "text-gray-500 bg-gray-500/10"}
                                                                    `}
                                                                >
                                                                    Action: {log.action.replaceAll("_", " ")}
                                                                </p> */}

                                                                {/* Contextual Badges based on action */}
                                                                <div className="flex flex-wrap gap-2">
                                                                    {/* List Context */}
                                                                    {(log.action === "added_to_list" || log.action === "removed_from_list") && log.meta?.listName && (
                                                                        <div className="flex items-center gap-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/50 rounded-lg">
                                                                            <ListChecks className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                                                                            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase truncate max-w-[200px]">List: {log.meta.listName}</span>
                                                                        </div>
                                                                    )}

                                                                    {/* Campaign Context */}
                                                                    {(log.action?.startsWith("campaign_")) && log.meta?.campaignName && (
                                                                        <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-lg">
                                                                            <Send className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                                                                            <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase truncate max-w-[200px]">Campaign: {log.meta.campaignName}</span>
                                                                        </div>
                                                                    )}

                                                                    {/* Generic Metadata Note */}
                                                                    {log.meta?.note && (
                                                                        <div className="flex items-center gap-2 p-2 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg">
                                                                            <Tag className="h-3.5 w-3.5 text-zinc-500" />
                                                                            <span className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase">{log.meta.note}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Grid Information */}
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                {/* Performed By Section */}
                                                                <div className="p-3 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800">
                                                                    <div className="flex items-center gap-2 mb-2 text-zinc-500">
                                                                        <User className="h-3 w-3" />
                                                                        <span className="text-[10px] font-bold uppercase tracking-wider">Performed By</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                                                                            {log.performedBy?.name?.charAt(0) || <User className="h-3 w-3" />}
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-50">
                                                                                {log.performedBy?.name || "System"}
                                                                            </p>
                                                                            <p className="text-[9px] text-zinc-500 uppercase font-bold">
                                                                                {log.performedBy?.type || "Automated"}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Metadata/System Section */}
                                                                <div className="p-3 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800">
                                                                    <div className="flex items-center gap-2 mb-2 text-zinc-500">
                                                                        <Clock className="h-3 w-3" />
                                                                        <span className="text-[10px] font-bold uppercase tracking-wider">Timestamp</span>
                                                                    </div>
                                                                    <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-50">
                                                                        {new Date(log.createdAt).toLocaleString()}
                                                                    </p>
                                                                    {log.requestId && (
                                                                        <div className="mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                                                                            <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">Request ID</p>
                                                                            <code className="text-[11px] font-mono bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded text-primary block w-fit select-all">
                                                                                {log.requestId.toUpperCase()}
                                                                            </code>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Changes Section */}
                                                            {log.changes && log.changes.length > 0 && (
                                                                <div className="p-3 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800">
                                                                    <div className="flex items-center gap-2 mb-3 text-zinc-500">
                                                                        <History className="h-3 w-3" />
                                                                        <span className="text-[10px] font-bold uppercase tracking-wider">Field Changes</span>
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        {log.changes.map((change: any, idx: number) => (
                                                                            <div key={idx} className="flex flex-col gap-1 border-b border-zinc-100 dark:border-zinc-800 last:border-0 pb-2 last:pb-0">
                                                                                <span className="text-[10px] font-bold text-primary uppercase">{change.field}</span>
                                                                                <div className="flex items-center gap-2 overflow-hidden min-h-[28px]">
                                                                                    {/* Old Value */}
                                                                                    <div className="min-w-0 flex-1 px-2 py-1 bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 rounded text-[11px] font-medium flex items-center">
                                                                                        {renderValue(change.field, change.oldValue)}
                                                                                    </div>

                                                                                    <ChevronRight className="h-3 w-3 text-zinc-300 shrink-0" />

                                                                                    {/* New Value */}
                                                                                    <div className="min-w-0 flex-1 px-2 py-1 bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 rounded text-[11px] font-medium flex items-center">
                                                                                        {renderValue(change.field, change.newValue)}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* 🔥 Pagination Bar */}
            {activityPagination && activityPagination.pages > 1 && (
                <div className="sticky bottom-0 bg-white dark:bg-zinc-950 px-6 py-3 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between shrink-0">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                        Page {activityPagination.page} of {activityPagination.pages}
                    </span>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            disabled={currentPage === 1 || loading}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            disabled={currentPage === activityPagination.pages || loading}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </TabsContent>
    );
};

export default ActivityTimelineTab;